import type { CompletionContext, CompletionResult, Completion } from '@codemirror/autocomplete';
import type { CatalogColumn, CatalogTable } from '@/types/api';

/**
 * Autocomplete fed from the live catalog.
 *
 * Two behaviours matter to an analyst: after `schema.` only that schema's
 * tables are offered, and after `table.` (or an alias bound in the FROM clause)
 * only that table's columns are. Everything else falls back to a merged list.
 */

export interface CatalogSnapshot {
  readonly tables: readonly CatalogTable[];
  readonly columns: readonly CatalogColumn[];
}

const DUCKDB_FUNCTIONS: readonly string[] = [
  'count',
  'sum',
  'avg',
  'min',
  'max',
  'median',
  'quantile_cont',
  'stddev',
  'approx_count_distinct',
  'list',
  'string_agg',
  'row_number',
  'rank',
  'dense_rank',
  'lag',
  'lead',
  'date_trunc',
  'date_part',
  'strftime',
  'strptime',
  'epoch_ms',
  'regexp_matches',
  'regexp_extract',
  'coalesce',
  'nullif',
  'try_cast',
  'read_parquet',
  'read_csv_auto',
  'read_json',
  'unnest',
  'histogram',
  'width_bucket',
];

const SQL_KEYWORDS: readonly string[] = [
  'SELECT',
  'FROM',
  'WHERE',
  'GROUP BY',
  'ORDER BY',
  'HAVING',
  'LIMIT',
  'OFFSET',
  'JOIN',
  'LEFT JOIN',
  'INNER JOIN',
  'FULL OUTER JOIN',
  'ON',
  'AS',
  'WITH',
  'UNION ALL',
  'DISTINCT',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'QUALIFY',
  'PIVOT',
  'UNPIVOT',
  'USING SAMPLE',
];

function completion(label: string, type: string, detail?: string): Completion {
  return detail === undefined ? { label, type } : { label, type, detail };
}

/** Aliases bound in FROM/JOIN clauses, so `p.` can resolve to a real table. */
export function collectAliases(sql: string): Map<string, string> {
  const aliases = new Map<string, string>();
  const pattern =
    /\b(?:from|join)\s+((?:"[^"]+"|[A-Za-z_][\w$]*)(?:\.(?:"[^"]+"|[A-Za-z_][\w$]*))*)\s*(?:as\s+)?([A-Za-z_][\w$]*)?/gi;

  for (const match of sql.matchAll(pattern)) {
    const qualified = match[1];
    if (!qualified) continue;
    const parts = qualified.split('.').map((part) => part.replace(/"/g, ''));
    const table = parts[parts.length - 1];
    if (!table) continue;

    const alias = match[2];
    const RESERVED = new Set(['where', 'group', 'order', 'limit', 'on', 'join', 'left', 'inner', 'full', 'union', 'having', 'qualify', 'using']);
    if (alias && !RESERVED.has(alias.toLowerCase())) {
      aliases.set(alias.toLowerCase(), table);
    }
    aliases.set(table.toLowerCase(), table);
  }

  return aliases;
}

export function createCatalogCompletion(
  getCatalog: () => CatalogSnapshot,
): (context: CompletionContext) => CompletionResult | null {
  return (context: CompletionContext): CompletionResult | null => {
    const { tables, columns } = getCatalog();

    // Qualified: `something.` -- resolve the qualifier before offering anything.
    const qualified = context.matchBefore(/(?:"[^"]*"|[A-Za-z_][\w$]*)\.[\w$]*/);
    if (qualified) {
      const text = qualified.text;
      const dot = text.lastIndexOf('.');
      const qualifier = text.slice(0, dot).replace(/"/g, '').toLowerCase();
      const from = qualified.from + dot + 1;

      const schemaTables = tables.filter((table) => table.schema.toLowerCase() === qualifier);
      if (schemaTables.length > 0) {
        return {
          from,
          options: schemaTables.map((table) =>
            completion(table.name, 'class', `${table.kind} · ${table.schema}`),
          ),
          validFor: /^[\w$]*$/,
        };
      }

      const aliases = collectAliases(context.state.doc.toString());
      const resolved = aliases.get(qualifier) ?? qualifier;
      const tableColumns = columns.filter(
        (column) => column.table.toLowerCase() === resolved.toLowerCase(),
      );
      if (tableColumns.length > 0) {
        return {
          from,
          options: tableColumns.map((column) =>
            completion(
              column.name,
              'property',
              column.isPartitionKey ? `${column.typeText} · partition` : column.typeText,
            ),
          ),
          validFor: /^[\w$]*$/,
        };
      }

      return null;
    }

    const word = context.matchBefore(/[\w$]+/);
    if (!word && !context.explicit) return null;
    const from = word?.from ?? context.pos;

    const seenColumns = new Set<string>();
    const columnOptions: Completion[] = [];
    for (const column of columns) {
      if (seenColumns.has(column.name)) continue;
      seenColumns.add(column.name);
      columnOptions.push(completion(column.name, 'property', column.typeText));
    }

    const options: Completion[] = [
      ...tables.map((table) =>
        completion(`${table.schema}.${table.name}`, 'class', `${table.kind} · ${table.database}`),
      ),
      ...columnOptions,
      ...DUCKDB_FUNCTIONS.map((name) => completion(name, 'function', 'function')),
      ...SQL_KEYWORDS.map((name) => completion(name, 'keyword')),
    ];

    return { from, options, validFor: /^[\w$]*$/ };
  };
}
